/**
 * ThemeDropdown Component
 * 
 * Theme customization dropdown with all controls:
 * - Theme type selection (Default/Sepia/Dracula)
 * - Mode toggle (Light/Dark)
 * - Accent color selection
 * - Text color customization
 * - Reset functionality
 */

import React, { useState } from 'react';
import { FaSun, FaMoon, FaEyeDropper } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { useColorScheme } from '../../contexts/ColorSchemeContext';
import CustomColorPicker from './CustomColorPicker';
import { ACCENT_COLOR_OPTIONS, getTextColorOptions } from './navigationConfig';

interface ThemeDropdownProps {
  isOpen: boolean;
  customColor: string;
  setCustomColorValue: (color: string) => void;
  onColorSchemeChange: (scheme: string) => void;
}

const ThemeDropdown: React.FC<ThemeDropdownProps> = ({
  isOpen,
  customColor,
  setCustomColorValue,
  onColorSchemeChange
}) => {
  const {
    theme,
    mode,
    colorScheme,
    titleColorOverride,
    bodyColorOverride,
    setTheme,
    setMode,
    setCustomColor,
    setTitleColor,
    setBodyTextColor,
    clearTextColorOverrides
  } = useTheme();

  const { getColorClasses } = useColorScheme();
  const colors = getColorClasses();

  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [showTitlePicker, setShowTitlePicker] = useState(false);
  const [showBodyPicker, setShowBodyPicker] = useState(false);

  const textColorOptions = getTextColorOptions(theme, mode);

  if (!isOpen) return null;

  return (
    <div className={`absolute top-full right-0 mt-2 w-72 rounded-lg shadow-xl border transition-all duration-300 ${colors.cardBackground} ${colors.borderColor} z-50`}>
      <div className="p-4 space-y-4">
        {/* Theme Type Selector */}
        <div>
          <h3 className={`text-sm font-semibold mb-3 ${colors.textPrimary}`}>
            Theme Type
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setTheme('default')}
              className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                theme === 'default'
                  ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                  : 'border-gray-300 dark:border-gray-600 hover:scale-105'
              }`}
              style={{ backgroundColor: '#FFFFFF' }}
              title="Default Theme"
            >
              <span className="text-xs text-gray-800 font-bold">D</span>
            </button>
            <button
              onClick={() => setTheme('sepia')}
              className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                theme === 'sepia'
                  ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                  : 'border-gray-300 dark:border-gray-600 hover:scale-105'
              }`}
              style={{ backgroundColor: '#D2B48C' }}
              title="Sepia Theme"
            >
              <span className="text-xs text-amber-800 font-bold">S</span>
            </button>
            <button
              onClick={() => setTheme('dracula')}
              className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                theme === 'dracula'
                  ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                  : 'border-gray-300 dark:border-gray-600 hover:scale-105'
              }`}
              style={{ backgroundColor: '#282A36' }}
              title="Dracula Theme"
            >
              <span className="text-xs text-purple-300 font-bold">D</span>
            </button>
          </div>
        </div>

        {/* Light/Dark Mode Toggle */}
        <div>
          <h3 className={`text-sm font-semibold mb-3 ${colors.textPrimary}`}>
            Mode
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setMode('light')}
              className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                mode === 'light'
                  ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                  : 'border-gray-300 dark:border-gray-600 hover:scale-105'
              }`}
              style={{ backgroundColor: '#FFFFFF' }}
              title="Light Mode"
            >
              <FaSun className="w-3 h-3 text-yellow-500" />
            </button>
            <button
              onClick={() => setMode('dark')}
              className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                mode === 'dark'
                  ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                  : 'border-gray-300 dark:border-gray-600 hover:scale-105'
              }`}
              style={{ backgroundColor: '#1F2937' }}
              title="Dark Mode"
            >
              <FaMoon className="w-3 h-3 text-blue-300" />
            </button>
          </div>
        </div>

        {/* Accent Color Selector */}
        <div className="relative">
          <h3 className={`text-sm font-semibold mb-3 ${colors.textPrimary}`}>
            Accent Color
          </h3>
          <div className="flex items-center space-x-2">
            {ACCENT_COLOR_OPTIONS.slice(0, 4).map((option) => (
              <button
                key={option.key}
                onClick={() => onColorSchemeChange(option.key!)}
                className={`w-8 h-8 rounded border-2 transition-all duration-300 ${
                  colorScheme === option.key
                    ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                    : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                }`}
                style={{ backgroundColor: option.color }}
                title={option.name}
              />
            ))}
            <button
              onClick={() => setShowCustomPicker(!showCustomPicker)}
              className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center relative ${
                colorScheme === 'custom'
                  ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                  : 'border-gray-300 dark:border-gray-600 hover:scale-105'
              }`}
              style={{ backgroundColor: customColor }}
              title="Custom Color"
            >
              <FaEyeDropper className="w-3 h-3 text-white" />
            </button>
            <CustomColorPicker
              isOpen={showCustomPicker}
              onClose={() => setShowCustomPicker(false)}
              currentColor={customColor}
              onChange={(color) => {
                setCustomColorValue(color);
                setCustomColor(color);
                onColorSchemeChange('custom');
              }}
              label="Custom Accent Color"
            />
          </div>
        </div>

        {/* Text Color Customization */}
        <div>
          <h3 className={`text-sm font-semibold mb-3 ${colors.textPrimary}`}>
            Text Colors {(titleColorOverride || bodyColorOverride) && <span className="text-xs font-normal ml-1">(Custom)</span>}
          </h3>

          {/* Title Color */}
          <div className="mb-3">
            <label className={`text-xs ${colors.textSecondary} mb-1 block`}>Title Color</label>
            <div className="flex items-center space-x-2">
              {textColorOptions.title.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => setTitleColor(option.color)}
                  className={`w-6 h-6 rounded border-2 transition-all duration-300 ${
                    titleColorOverride === option.color
                      ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                      : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                  }`}
                  style={{ backgroundColor: option.color }}
                  title={option.name}
                />
              ))}
              <button
                onClick={() => setShowTitlePicker(!showTitlePicker)}
                className={`w-6 h-6 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                  showTitlePicker
                    ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                    : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                }`}
                style={{ backgroundColor: titleColorOverride || '#666' }}
                title="Custom Title Color"
              >
                <FaEyeDropper className="w-2 h-2 text-white" />
              </button>
              <CustomColorPicker
                isOpen={showTitlePicker}
                onClose={() => setShowTitlePicker(false)}
                currentColor={titleColorOverride || '#000000'}
                onChange={setTitleColor}
                label="Title Color"
              />
            </div>
          </div>

          {/* Body Color */}
          <div className="mb-2">
            <label className={`text-xs ${colors.textSecondary} mb-1 block`}>Body Color</label>
            <div className="flex items-center space-x-2">
              {textColorOptions.body.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => setBodyTextColor(option.color)}
                  className={`w-6 h-6 rounded border-2 transition-all duration-300 ${
                    bodyColorOverride === option.color
                      ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                      : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                  }`}
                  style={{ backgroundColor: option.color }}
                  title={option.name}
                />
              ))}
              <button
                onClick={() => setShowBodyPicker(!showBodyPicker)}
                className={`w-6 h-6 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                  showBodyPicker
                    ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                    : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                }`}
                style={{ backgroundColor: bodyColorOverride || '#999' }}
                title="Custom Body Color"
              >
                <FaEyeDropper className="w-2 h-2 text-white" />
              </button>
              <CustomColorPicker
                isOpen={showBodyPicker}
                onClose={() => setShowBodyPicker(false)}
                currentColor={bodyColorOverride || '#666666'}
                onChange={setBodyTextColor}
                label="Body Text Color"
              />
            </div>
          </div>

          {/* Clear Overrides */}
          {(titleColorOverride || bodyColorOverride) && (
            <button
              onClick={clearTextColorOverrides}
              className={`text-xs ${colors.textSecondary} hover:${colors.text} underline mt-1`}
            >
              Reset to theme colors
            </button>
          )}
        </div>

        {/* Reset Button */}
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => {
              onColorSchemeChange('gold');
              setTheme('default');
              setMode('dark');
              clearTextColorOverrides();
            }}
            className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${colors.textSecondary} hover:${colors.cardBackground} border border-gray-300 dark:border-gray-700`}
          >
            Reset to Default
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThemeDropdown;

